import { IFORM } from 'featureLib/FormBuilder';
import { productOptions, typeOfOrderOptions } from 'featureLib/OrderDetails/formConfig';
import { getProductType, isBulkUpgrade, isShiftingJourney, PRODUCT_ALIAS_MAP, TYPEOF_ORDER } from 'common/constants/app.constants';
import { SINGLE_SITE, RADIO_OPTIONS as siteTypeOptions } from 'home/view/manage-feasibility/constants';
import { getFromSessionStorage, setInSessionStorage } from 'common/utils/app.utils';
import { MODULE_ID, PROFILE_DATA, SELECTED_PARTY_ACCOUNT, SELECTED_PARTY_DATA } from 'home/common/constants/constants';
import { getCurrentLead, saveSelectedProductType } from 'tokenGeneration/lead.util';

export const getDefaultValue = (formSchema: any, defaultValue: any, disableAll: boolean = false) => {
  return Object.keys(formSchema).reduce((acc: IFORM, key: any) => {
    const value = defaultValue[key] ?? formSchema[key].value;
    acc[key] = { ...formSchema[key], value, disabled: disableAll };
    return acc;
  }, {});
};

export const getFormValue = (formSchema: IFORM) => {
  return Object.keys(formSchema).reduce((acc: IFORM, key: any) => {
    const value = formSchema[key].value;
    acc[key] = value;
    return acc;
  }, {});
};

const getDefaultTypeOfOrder = () => {
  if (isBulkUpgrade()) {
    return typeOfOrderOptions.find((item: any) => item?.key === TYPEOF_ORDER.UPGRADE);
  }

  if (isShiftingJourney()) {
    return typeOfOrderOptions.find((item: any) => item?.key === TYPEOF_ORDER.SHIFTING);
  }

  return typeOfOrderOptions[0];
};

export const getSiteOptionsAndValue = (orderSubType: string, journeyType: string, product: string, orderType?: string) => {
  let siteListOptions = siteTypeOptions.filter((item: any) => item?.orderType?.includes(orderSubType ?? getDefaultTypeOfOrder()?.key) && item?.supportedProducts.includes(product));
  const isNewPreOrder = orderType === 'PRE_ORDER' && orderSubType === 'NEW';
  const isUpgradePreOrder = orderType === 'PRE_ORDER' && orderSubType === 'UPGRADE';
  if (isNewPreOrder) {
    siteListOptions = siteTypeOptions.filter((item: any) => item?.orderType?.includes(TYPEOF_ORDER.NEW_PRE_ORDER) && item?.supportedProducts.includes(product));
  }
  if (isUpgradePreOrder) {
    siteListOptions = siteTypeOptions.filter((item: any) => item?.orderType?.includes(TYPEOF_ORDER.UPGRADE_PRE_ORDER) && item?.supportedProducts.includes(product));
  }
  let siteType = siteListOptions.find((item) => item?.id === journeyType);
  if (siteListOptions.length === 1) {
    siteType = siteListOptions[0];
  }
  return {
    siteListOptions,
    siteType
  };
};

const getContactFormData = (leadData: any) => {
  const {
    customer: { authorizedSignatory: { firstName = '', lastName = '', phoneNumber, email } } = {
      authorizedSignatory: {
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: ''
      }
    }
  } = leadData;

  const userProfile = getFromSessionStorage(MODULE_ID, PROFILE_DATA);
  const {
    personalInfo: { communicationNumber, emailId, phoneNumber: userPhoneNumber, userName }
  } = userProfile;

  return {
    fullName: firstName ? firstName + (' ' + lastName ?? '') : userName,
    phoneNumber: phoneNumber || userPhoneNumber || communicationNumber,
    emailAddress: email || emailId
  };
};

export const convertLsiToOrderDetailsFormData = (lsiData: any) => {
  // set Order form Data
  const {
    product: currentProductType,
    customer: { partyId, name }
  } = lsiData;

  const selectedAccount = partyId
    ? { partyId, partyName: name }
    : {
        partyName: '',
        partyId: ''
      };

  const productType = productOptions.find((product: { displayName: string; key: string }) => currentProductType === product.key);
  const orderTypeOptions = typeOfOrderOptions.filter((orderType) => orderType.products.includes(productType?.key ?? ''));
  const typeOfOrder = orderTypeOptions.find((item) => item.key === (getCurrentLead()?.orderSubType ?? getDefaultTypeOfOrder()?.key ?? TYPEOF_ORDER.UPGRADE));
  const siteListOptions = isShiftingJourney() ? siteTypeOptions.filter((item) => item.id === SINGLE_SITE) : siteTypeOptions;
  const siteType = siteListOptions.find((item) => item?.id === SINGLE_SITE);
  const defaultOrderNickName = name
    ?.substr(0, 4)
    ?.toUpperCase()
    .concat(' ', PRODUCT_ALIAS_MAP[currentProductType as keyof typeof PRODUCT_ALIAS_MAP])
    .concat(' ', typeOfOrder?.displayName ?? typeOfOrder?.key);

  // set Contact Form Data
  const userProfile = getFromSessionStorage(MODULE_ID, PROFILE_DATA);
  const {
    personalInfo: { communicationNumber, emailId, phoneNumber, userName }
  } = userProfile;

  // update product type and account info after lsi data fetch
  saveSelectedProductType(currentProductType);
  setInSessionStorage(MODULE_ID, SELECTED_PARTY_ACCOUNT, partyId);
  setInSessionStorage(MODULE_ID, SELECTED_PARTY_DATA, {
    partyName: name,
    partyId
  });

  return {
    orderForm: {
      selectedAccount,
      productType,
      typeOfOrder,
      orderNickName: defaultOrderNickName,
      siteType,
      orderTypeOptions,
      siteListOptions
    },
    contactForm: {
      fullName: userName,
      phoneNumber: phoneNumber || communicationNumber,
      emailAddress: emailId
    }
  };
};

export const convertLeadToOrderDetailsFormData = (leadData: any) => {
  const {
    serviceType,
    orderType, // CHANGE_ORDER/ NEW
    orderSubType, // UPGRADE/ NEW
    orderNickName,
    loiAvailable,
    journeyType,
    customer: { partyId, authorizedSignatory: { companyName } } = {
      partyId: '',
      authorizedSignatory: {
        companyName
      }
    }
  } = leadData;

  const currentProductType = serviceType ?? getProductType();

  const productType = productOptions.find((product: { displayName: string; key: string }) => currentProductType === product.key);
  const orderTypeOptions = typeOfOrderOptions.filter((orderType) => orderType.products.includes(productType?.key ?? 'DEDICATED_INTERNET'));
  let typeOfOrder = orderTypeOptions.find((item) => item?.key === orderSubType) ?? getDefaultTypeOfOrder();
  const isNewPreOrder = orderType === 'PRE_ORDER' && orderSubType === 'NEW';
  const isUpgradePreOrder = orderType === 'PRE_ORDER' && orderSubType === 'UPGRADE';
  if (isNewPreOrder) {
    typeOfOrder = orderTypeOptions.find((item) => item?.key === TYPEOF_ORDER.NEW_PRE_ORDER);
  }
  if (isUpgradePreOrder) {
    typeOfOrder = orderTypeOptions.find((item) => item?.key === TYPEOF_ORDER.UPGRADE_PRE_ORDER);
  }
  const { siteListOptions, siteType } = getSiteOptionsAndValue(orderSubType, journeyType, currentProductType, orderType);

  const selectedAccount = partyId
    ? { partyId, partyName: companyName }
    : {
        partyName: '',
        partyId: ''
      };

  return {
    orderForm: {
      selectedAccount,
      productType,
      typeOfOrder,
      orderNickName,
      siteType,
      orderTypeOptions,
      siteListOptions,
      loiAvailable
    },
    contactForm: getContactFormData(leadData)
  };
};
